import React, { useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const TravelStatistics = () => {
  const [timeFilter, setTimeFilter] = useState('week');

  // Données simulées
  const weekData = [
    { date: 'Lun', aerien: 450, terrestre: 780, interne: 620 },
    { date: 'Mar', aerien: 520, terrestre: 890, interne: 700 },
    { date: 'Mer', aerien: 480, terrestre: 760, interne: 650 },
    { date: 'Jeu', aerien: 590, terrestre: 920, interne: 780 },
    { date: 'Ven', aerien: 620, terrestre: 1100, interne: 850 },
    { date: 'Sam', aerien: 400, terrestre: 680, interne: 580 },
    { date: 'Dim', aerien: 350, terrestre: 580, interne: 490 }
  ];

  const monthData = [
    { date: 'Sem 1', aerien: 2800, terrestre: 4500, interne: 3200 },
    { date: 'Sem 2', aerien: 3100, terrestre: 4800, interne: 3600 },
    { date: 'Sem 3', aerien: 2900, terrestre: 4600, interne: 3400 },
    { date: 'Sem 4', aerien: 3300, terrestre: 5100, interne: 3800 }
  ];

  const yearData = [
    { date: 'Jan', aerien: 12000, terrestre: 18500, interne: 14200 },
    { date: 'Fév', aerien: 13200, terrestre: 19200, interne: 15100 },
    { date: 'Mar', aerien: 14500, terrestre: 20100, interne: 16300 },
    { date: 'Avr', aerien: 13800, terrestre: 19800, interne: 15800 },
    { date: 'Mai', aerien: 15200, terrestre: 21500, interne: 17200 },
    { date: 'Juin', aerien: 16100, terrestre: 22300, interne: 18100 },
    { date: 'Juil', aerien: 15800, terrestre: 21900, interne: 17800 },
    { date: 'Août', aerien: 14900, terrestre: 20800, interne: 16900 },
    { date: 'Sep', aerien: 15500, terrestre: 21400, interne: 17400 },
    { date: 'Oct', aerien: 16300, terrestre: 22100, interne: 18300 },
    { date: 'Nov', aerien: 15900, terrestre: 21700, interne: 17900 },
    { date: 'Déc', aerien: 16800, terrestre: 23100, interne: 19200 }
  ];

  const getData = () => {
    switch (timeFilter) {
      case 'week':
        return weekData;
      case 'month':
        return monthData;
      case 'year':
        return yearData;
      default:
        return weekData;
    }
  };

  // Couleurs définies pour chaque type de voyage
  const colors = {
    aerien: '#f97316',
    terrestre: '#15803d',
    interne: '#6366f1'
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-4 border rounded-lg shadow-lg">
          <p className="font-bold text-gray-800 mb-2">{label}</p>
          {payload.map((entry) => {
            // Utilisation des couleurs correspondantes pour chaque type de voyage
            const typeName = entry.dataKey === 'aerien' ? 'Voyages Aériens' :
                           entry.dataKey === 'terrestre' ? 'Voyages Terrestres' :
                           'Voyages Internes';
            return (
              <p 
                key={entry.dataKey} 
                style={{ color: colors[entry.dataKey] }} 
                className="text-sm"
              >
                {typeName}: {entry.value.toLocaleString()} voyageurs
              </p>
            );
          })}
        </div>
      );
    }
    return null;
  };

  const calculateTotal = (data) => {
    return data.reduce((acc, curr) => 
      acc + curr.aerien + curr.terrestre + curr.interne, 0
    ).toLocaleString();
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-bold">Statistiques des Voyages</h3>
        <div className="flex space-x-4">
          <button
            onClick={() => setTimeFilter('week')}
            className={`px-4 py-2 rounded-lg ${
              timeFilter === 'week'
                ? 'bg-green-600 text-white'
                : 'bg-gray-100 text-gray-600'
            }`}
          >
            Semaine
          </button>
          <button
            onClick={() => setTimeFilter('month')}
            className={`px-4 py-2 rounded-lg ${
              timeFilter === 'month'
                ? 'bg-green-600 text-white'
                : 'bg-gray-100 text-gray-600'
            }`}
          >
            Mois
          </button>
          <button
            onClick={() => setTimeFilter('year')}
            className={`px-4 py-2 rounded-lg ${
              timeFilter === 'year'
                ? 'bg-green-600 text-white'
                : 'bg-gray-100 text-gray-600'
            }`}
          >
            Année
          </button>
        </div>
      </div>

      <div className="text-sm text-gray-500 mb-4">
        Total: {calculateTotal(getData())} voyageurs
      </div>

      <div className="h-[400px]">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={getData()}
            margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 5,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Line 
              type="monotone" 
              dataKey="aerien" 
              name="Voyages Aériens"
              stroke={colors.aerien}
              strokeWidth={2}
              dot={{ r: 4 }}
            />
            <Line 
              type="monotone" 
              dataKey="terrestre" 
              name="Voyages Terrestres"
              stroke={colors.terrestre}
              strokeWidth={2}
              dot={{ r: 4 }}
            />
            <Line 
              type="monotone" 
              dataKey="interne" 
              name="Voyages Internes"
              stroke={colors.interne}
              strokeWidth={2}
              dot={{ r: 4 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-4 text-sm text-gray-600">
        <div className="space-y-1">
          <div className="font-semibold" style={{ color: colors.aerien }}>Voyages Aériens</div>
          <div>Tendance: +12% {timeFilter === 'week' ? 'cette semaine' : timeFilter === 'month' ? 'ce mois' : 'cette année'}</div>
        </div>
        <div className="space-y-1">
          <div className="font-semibold" style={{ color: colors.terrestre }}>Voyages Terrestres</div>
          <div>Tendance: +8% {timeFilter === 'week' ? 'cette semaine' : timeFilter === 'month' ? 'ce mois' : 'cette année'}</div>
        </div>
        <div className="space-y-1">
          <div className="font-semibold" style={{ color: colors.interne }}>Voyages Internes</div>
          <div>Tendance: +15% {timeFilter === 'week' ? 'cette semaine' : timeFilter === 'month' ? 'ce mois' : 'cette année'}</div>
        </div>
      </div>
    </div>
  );
};

export default TravelStatistics;